import { GenericItemLSS } from '../ListSearchSelect';

export type PossibleItemLSS = string | GenericItemLSS;

/**
 * Helper to extract author name fields from a PossibleItemLSS.
 * Returns an object with firstName, lastName, and screenName.
 */
function extractAuthorNames(item: PossibleItemLSS): { firstName?: string; lastName?: string; screenName?: string } {
  if (typeof item !== 'object') return {};
  return {
    firstName: item.first_name,
    lastName: item.last_name,
    screenName: item.screen_name,
  };
}

function getLabelText(selection: PossibleItemLSS): string {
  if (typeof selection === 'object') {
    return selection?.label || selection?.path || selection?.name || '';
  }
  if (typeof selection === 'string') {
    return String(selection);
  }
  return '';
}

/**
 * Gets the primary display text for an author item in the ListSearchSelect component.
 * Prefers to show the full name (first + last) if available, otherwise falls back to screen_name.
 * Used for the main label in author search results when authorDisplay is enabled.
 */
export function getAuthorPrimaryText(item: PossibleItemLSS): string {
  if (typeof item !== 'object') return getLabelText(item);

  const { firstName, lastName, screenName } = extractAuthorNames(item);

  if (firstName || lastName) {
    const name = [firstName, lastName].filter(Boolean).join(' ');
    return name;
  }

  return screenName || getLabelText(item);
}

/**
 * Gets the secondary display text for an author item in the ListSearchSelect component.
 * Shows "@screen_name" as secondary text if the primary text is the full name, otherwise returns null.
 * Used for additional context in author search results when authorDisplay is enabled.
 */
export function getAuthorSecondaryText(item: PossibleItemLSS): string | null {
  if (typeof item !== 'object') return null;

  const { firstName, lastName, screenName } = extractAuthorNames(item);

  if (firstName || lastName) {
    return screenName ? `@${screenName}` : null;
  }

  return null;
}

/**
 * Gets the account type chip information for an author item in the ListSearchSelect component.
 * Returns an object with label (capitalized account type) and color (Material-UI color string) for the chip,
 * or null if the item doesn't have an account_type or isn't an object.
 * Used to display account type badges in author search results when authorDisplay is enabled.
 */
export function getAuthorAccountTypeChip(
  item: PossibleItemLSS,
): { label: string; color: string } | null {
  if (typeof item !== 'object' || !item.account_type) return null;

  const accountType = String(item.account_type).toLowerCase();
  let color = 'default';

  switch (accountType) {
    case 'admin':
      color = 'error';
      break;
    case 'staff':
      color = 'primary';
      break;
    default:
      color = 'default';
  }

  return {
    label: accountType.charAt(0).toUpperCase() + accountType.slice(1),
    color,
  };
}
